// Timing engine — converts an annual budget figure into a 12-element
// monthly allocation according to the line's TimingProfile.
//
// The engine is deliberately small: every profile kind reduces to a
// 12-element weight vector (one number per calendar month, Jan first),
// which we then scale to the line's annual amount. The orchestrator
// stitches multiple lines together over a multi-month horizon.

import type { TimingProfile, TimingProfileKind } from '../../cashflowTypes';

export const DEFAULT_PROFILE_KIND: TimingProfileKind = 'historical';

/** Returns a 12-element weight vector (Jan..Dec) summing to ~1.0,
 *  representing how the annual amount should be distributed across
 *  the 12 calendar months.
 *
 *  Note: `historical` kind requires the orchestrator to substitute the
 *  line's derived weights into customMonths before calling this — when
 *  it appears here without customMonths set, we fall back to flat
 *  monthly (the line had no usable history). */
export function profileWeights(profile: TimingProfile | null): number[] {
  const kind = profile?.kind ?? DEFAULT_PROFILE_KIND;
  if (kind === 'monthly' || (kind === 'historical' && !profile?.customMonths)) {
    return Array.from({ length: 12 }, () => 1 / 12);
  }
  if (kind === 'historical' && profile?.customMonths) {
    // Substituted historical weights — fall through to custom handling.
    return normaliseCustom(profile.customMonths);
  }
  if (kind === 'quarterly') {
    const anchors =
      profile?.anchorMonths && profile.anchorMonths.length > 0
        ? profile.anchorMonths
        : [1, 4, 7, 10];
    const weights = Array.from({ length: 12 }, () => 0);
    const per = 1 / anchors.length;
    anchors.forEach((m) => {
      const idx = clampMonth(m) - 1;
      weights[idx] = (weights[idx] ?? 0) + per;
    });
    return weights;
  }
  if (kind === 'annual') {
    const anchor = profile?.anchorMonths?.[0] ?? 1;
    const weights = Array.from({ length: 12 }, () => 0);
    weights[clampMonth(anchor) - 1] = 1;
    return weights;
  }
  // custom
  const raw = profile?.customMonths ?? Array.from({ length: 12 }, () => 1 / 12);
  return normaliseCustom(raw);
}

function normaliseCustom(raw: number[]): number[] {
  const total = raw.reduce((s, v) => s + Math.max(0, v), 0);
  if (total <= 0) return Array.from({ length: 12 }, () => 1 / 12);
  return raw.map((v) => Math.max(0, v) / total);
}

function clampMonth(m: number): number {
  if (!isFinite(m)) return 1;
  const n = Math.round(m);
  if (n < 1) return 1;
  if (n > 12) return 12;
  return n;
}

/** Allocates a single annual amount across the 12 calendar months
 *  for a given profile. Result has length 12, indexed by month-1. */
export function allocateAnnualToMonths(
  annualAmount: number,
  profile: TimingProfile | null,
): number[] {
  return profileWeights(profile).map((w) => annualAmount * w);
}

/** Builds a YYYY-MM key for a (year, month-1-based) pair. */
export function ymKey(year: number, monthIdx: number): string {
  const y = year + Math.floor(monthIdx / 12);
  const m = ((monthIdx % 12) + 12) % 12;
  return `${y}-${String(m + 1).padStart(2, '0')}`;
}

/** Parses YYYY-MM into [year, monthIdx-0-based]. */
export function parseYm(ym: string): [number, number] {
  const parts = ym.split('-').map((s) => parseInt(s, 10));
  const y = parts[0] ?? 0;
  const m = parts[1] ?? 1;
  return [y, (m || 1) - 1];
}

/** Generates a sequence of YYYY-MM strings starting from `startYm`,
 *  inclusive, of length `count`. */
export function generateMonthSequence(startYm: string, count: number): string[] {
  const [y, mIdx] = parseYm(startYm);
  return Array.from({ length: count }, (_, i) => ymKey(y, mIdx + i));
}

/** Distributes an annual amount across an arbitrary month-window using
 *  the profile's calendar-month weights. The window can span across
 *  multiple calendar years; weights are picked by the *month-of-year*
 *  of each window slot. */
export function allocateAcrossWindow(
  annualAmount: number,
  profile: TimingProfile | null,
  windowYms: string[],
): Record<string, number> {
  const weights = profileWeights(profile);
  const result: Record<string, number> = {};
  windowYms.forEach((ym) => {
    const [, mIdx] = parseYm(ym);
    result[ym] = (result[ym] ?? 0) + annualAmount * (weights[mIdx] ?? 0);
  });
  return result;
}
